/**
 * BudgetTransferDrawer Component
 * Create budget transfer request between line items
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { X, ArrowRightLeft } from 'lucide-react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormSelect, FormTextArea, CurrencyInput } from '../forms';
import type { BudgetLineItem, CreateTransferRequest } from '../../types/budget';

export interface BudgetTransferDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTransferRequest) => Promise<void>;
  lineItems: BudgetLineItem[];
  isLoading?: boolean;
}

// Validation schema
const transferSchema = Yup.object({
  fromLineItemId: Yup.string().required('Source line item is required'),
  toLineItemId: Yup.string()
    .required('Destination line item is required')
    .test('not-same', 'Source and destination must be different', function (value) {
      return value !== this.parent.fromLineItemId;
    }),
  amount: Yup.number()
    .required('Amount is required')
    .min(0.01, 'Amount must be greater than 0')
    .test('available-budget', 'Amount exceeds available budget', function (value) {
      const fromLineItemId = this.parent.fromLineItemId;
      if (!fromLineItemId || !value) return true;

      // This validation will be done on the server, but we can show a warning
      return true;
    }),
  reason: Yup.string()
    .required('Reason is required')
    .min(10, 'Please provide a detailed reason (at least 10 characters)'),
});

type FormData = {
  fromLineItemId: string;
  toLineItemId: string;
  amount: number;
  reason: string;
};

const BudgetTransferDrawer: React.FC<BudgetTransferDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  lineItems,
  isLoading = false,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(transferSchema),
    defaultValues: {
      fromLineItemId: '',
      toLineItemId: '',
      amount: 0,
      reason: '',
    },
  });

  const fromLineItemId = watch('fromLineItemId');
  const selectedFromItem = lineItems.find(item => item._id === fromLineItemId);
  const availableBudget = selectedFromItem ? selectedFromItem.variance : 0;

  const handleFormSubmit = async (data: FormData) => {
    try {
      await onSubmit(data as CreateTransferRequest);
      onClose();
      reset();
    } catch (error) {
      console.error('Error creating transfer:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  if (!isOpen) return null;

  const lineItemOptions = lineItems.map(item => ({
    value: item._id,
    label: `${item.costCode} - ${item.description} (Available: ${formatCurrency(item.variance)})`,
  }));

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Drawer - Small size for simple form */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <ArrowRightLeft className="h-5 w-5 text-brand-600" />
              <h2 className="text-lg font-semibold text-gray-900">Budget Transfer Request</h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Transfer budget from one line item to another. This requires approval before the
                  budget allocation is changed.
                </p>
              </div>

              {/* From Line Item */}
              <Controller
                name="fromLineItemId"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    label="From Line Item"
                    options={lineItemOptions}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.fromLineItemId}
                    required
                    placeholder="Select source line item"
                  />
                )}
              />

              {/* Available Budget Display */}
              {selectedFromItem && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Available Budget:</span>
                    <span
                      className={`text-lg font-semibold ${availableBudget > 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {formatCurrency(availableBudget)}
                    </span>
                  </div>
                  {availableBudget <= 0 && (
                    <p className="text-xs text-red-600 mt-2">
                      Warning: This line item is over budget. Transfer may require special approval.
                    </p>
                  )}
                </div>
              )}

              {/* To Line Item */}
              <Controller
                name="toLineItemId"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    label="To Line Item"
                    options={lineItemOptions}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.toLineItemId}
                    required
                    placeholder="Select destination line item"
                  />
                )}
              />

              {/* Amount */}
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    label="Transfer Amount"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.amount?.message}
                    required
                    placeholder="$0.00"
                  />
                )}
              />

              {/* Reason */}
              <Controller
                name="reason"
                control={control}
                render={({ field }) => (
                  <FormTextArea
                    label="Reason for Transfer"
                    {...field}
                    error={errors.reason}
                    required
                    placeholder="Provide a detailed explanation for this budget transfer..."
                    rows={4}
                  />
                )}
              />
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Submitting...' : 'Submit Transfer Request'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};

export default BudgetTransferDrawer;
